import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import Chart from 'chart.js/auto';
import { truncateString } from '../../helper';
import '../../tooltipPositioners';

function VerticalPercentageBarChart(props) {
  const { labels, data, images, Tooltip, extraDatasets = [], height = 280 } = props;
  const chartRef = useRef();
  const canvasRef = useRef(null);
  const [tooltipModel, setTooltipModel] = useState();
  const dataPoint = tooltipModel?.dataPoints[0];
  if (dataPoint) {
    // increase the length of label for tooltip to 500 characters.
    dataPoint.label = truncateString(labels?.[dataPoint?.dataIndex], 500);
  }
  const image = dataPoint && images?.[dataPoint?.dataIndex];

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: labels.map((label) => truncateString(label, 40)),
        datasets: [
          {
            data,
            label: 'Percentage',
            backgroundColor: '#3588d1',
            barPercentage: 0.9,
            maxBarThickness: 24,
          },
          ...extraDatasets,
          {
            data: new Array(data.length).fill(100),
            backgroundColor: '#f7f7f7',
            hoverBackgroundColor: '#f7f7f7',
            barPercentage: 0.9,
            maxBarThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            position: 'middle',
            filter: ({ datasetIndex }) => datasetIndex === 0,
            ...(Tooltip && {
              enabled: false,
              external: (context) => setTooltipModel({ ...context.tooltip }),
            }),
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { padding: 10, autoSkip: false },
            stacked: true,
          },
          y: {
            min: 0,
            max: 100,
            grid: { drawTicks: false },
            border: { display: false },
            ticks: {
              padding: 14,
              autoSkip: false,
              stepSize: 25,
              count: 5,
              // eslint-disable-next-line id-denylist
              callback: (tickValue) => `${tickValue}%`,
            },
          },
        },
      },
    });
  }, [data]);

  return (
    <>
      <div style={{ height: `${height}px` }}>
        <canvas className="engage-report-question-charts__canvas" ref={canvasRef} />
      </div>
      {Tooltip
        ? createPortal(
            <Tooltip tooltipModel={tooltipModel} canvasRef={canvasRef} image={image} />,
            document.body
          )
        : null}
    </>
  );
}

VerticalPercentageBarChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  extraDatasets: PropTypes.arrayOf(PropTypes.shape({})),
  images: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.number,
  Tooltip: PropTypes.func,
};

VerticalPercentageBarChart.defaultProps = {
  images: undefined,
  height: undefined,
  Tooltip: undefined,
  extraDatasets: undefined,
};

export default VerticalPercentageBarChart;
